import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import saveSurveyResult from '../utils/saveSurveyResult'
import uploadSurveyCameraRecording from '../utils/uploadSurveyCameraRecording'
import './eurobankImplicitRt.css'

const AD_URL = 'https://ai.surveyneuro.com/images/H-APOKALIPSI.mp4'
const SURVEY_CODE = 'eurobank_implicit_rt'
const DEADLINE_MS = 1800
const CALIBRATION_CLICKS_PER_POINT = 5
const CALIBRATION_POINTS = [
  { x: 10, y: 27 }, { x: 50, y: 27 }, { x: 90, y: 27 },
  { x: 10, y: 55 }, { x: 50, y: 55 }, { x: 90, y: 55 },
  { x: 10, y: 85 }, { x: 50, y: 85 }, { x: 90, y: 85 },
]
const MAX_GAZE_SAMPLES = 12000
const FACE_CONFIRMATION_MS = 1000
const FACE_MISSING_WARNING_MS = 1800
const MOTIVATION_OPTIONS = ['Καθόλου', 'Λίγο', 'Αρκετά', 'Πολύ', 'Πάρα πολύ']
const AGREEMENT_OPTIONS = ['Διαφωνώ απόλυτα', 'Μάλλον διαφωνώ', 'Ούτε συμφωνώ ούτε διαφωνώ', 'Μάλλον συμφωνώ', 'Συμφωνώ απόλυτα']
const ATTITUDE_STATEMENTS = [
  'Το συγκεκριμένο σενάριο με πείθει ότι η Eurobank έχει κάνει πολύ απλή τη διαδικασία απόκτησης στεγαστικού δανείου.',
  'Το συγκεκριμένο σενάριο με κάνει να αισθάνομαι ανακούφιση ότι πλέον το να πάρω στεγαστικό δάνειο δεν έχει χρονοβόρες και πολύπλοκες διαδικασίες.',
  'Το συγκεκριμένο σενάριο με κάνει να νιώθω ανακούφιση ότι έχω πάντα έναν ειδικό δίπλα μου όποτε τον χρειαστώ.',
  'Η δυνατότητα να πάρω στεγαστικό δάνειο από τη Eurobank με αίτηση από το κινητό με κάνει να θέλω να απευθυνθώ σε αυτήν για το στεγαστικό μου δάνειο.',
  'Παρά όσα παρουσιάζει το συγκεκριμένο σενάριο, η διαδικασία απόκτησης στεγαστικού δανείου εξακολουθεί να μου φαίνεται αγχωτική.',
  'Το συγκεκριμένο σενάριο δεν καταφέρνει να με καθησυχάσει ότι μια τόσο σημαντική διαδικασία μπορεί να γίνει με ασφάλεια χωρίς να χρειάζεται να βρίσκομαι σε κατάστημα της τράπεζας.',
  'Η δυνατότητα να κάνω την αίτηση και να ολοκληρώσω τη διαδικασία απόκτησης του στεγαστικού μου δανείου όποια στιγμή θέλω και από όπου θέλω με εξυπηρετεί και με απαλλάσσει από άγχος.',
]
const ADVERTISEMENTS = [
  { id: 'h_apokalipsi', url: AD_URL },
  { id: 'to_plakaki', url: 'https://ai.surveyneuro.com/images/TO-PLAKAKI.mp4' },
]

const ATTRIBUTES = [
  ['Εύκολο', 'Απλό', 'Χωρίς κόπο', 'Χωρίς ταλαιπωρία'],
  ['Κατανοητό', 'Σαφές', 'Ξεκάθαρο', 'Ευκολονόητο'],
  ['Καθησυχαστικό', 'Καθησυχάζει', 'Ασφαλές', 'Μου δίνει σιγουριά'],
  ['Ευχάριστο', 'Απολαυστικό', 'Ελκυστικό', 'Συμπαθητικό'],
  ['Πειστικό', 'Πιστευτό', 'Με πείθει', 'Ισχυρό επιχείρημα'],
  ['Αξιόπιστο', 'Έμπιστο', 'Φερέγγυο', 'Εμπνέει εμπιστοσύνη'],
  ['Πρωτοποριακό', 'Καινοτόμο', 'Σύγχρονο', 'Προοδευτικό'],
  ['Μου ταιριάζει', 'Είναι για μένα', 'Με αφορά', 'Σχετικό με μένα'],
  ['Με κινητοποιεί', 'Με παρακινεί', 'Με ενθαρρύνει', 'Με εμπνέει'],
  ['Νέα οπτική', 'Νέα προσέγγιση', 'Διαφορετική οπτική', 'Με κάνει να το δω αλλιώς'],
  ['Διαφορετικό', 'Ξεχωριστό', 'Διακριτό', 'Ξεχωρίζει'],
  ['Μοναδικό', 'Πρωτότυπο', 'Κάτι νέο', 'Μοναδική πρόταση'],
]

const PRACTICE_WORDS = ['Βουνό', 'Τετράδιο', 'Κήπος', 'Λάμπα', 'Σάντουιτς', 'Ποδήλατο']

function shuffle(items) {
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

function randomBetween(min, max) {
  return Math.round(min + Math.random() * (max - min))
}

function makeResponseId() {
  return `rt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function getCameraRecordingFormat() {
  if (typeof MediaRecorder === 'undefined') return null
  const candidates = [
    { mimeType: 'video/webm;codecs=vp8', extension: 'webm' },
    { mimeType: 'video/webm', extension: 'webm' },
    { mimeType: 'video/mp4', extension: 'mp4' },
  ]
  return candidates.find(({ mimeType }) => MediaRecorder.isTypeSupported(mimeType)) || { mimeType: '', extension: 'webm' }
}

function detectMobileBrowser() {
  if (typeof navigator === 'undefined') return false
  if (navigator.userAgentData?.mobile === true) return true
  const userAgent = navigator.userAgent || ''
  const iPadDesktopMode = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone|Mobile|Silk/i.test(userAgent) || iPadDesktopMode
}

export default function EurobankImplicitRt() {
  const responseId = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('responseId') || params.get('id') || makeResponseId()
  }, [])
  const adOrder = useMemo(() => shuffle(ADVERTISEMENTS), [])
  const trialSets = useMemo(() => adOrder.map(() => shuffle(ATTRIBUTES.flatMap((variants, attributeIndex) => (
    variants.map((stimulus, variationIndex) => ({ attributeIndex: attributeIndex + 1, variationIndex: variationIndex + 1, stimulus }))
  )))), [adOrder])
  const practiceTrials = useMemo(() => shuffle(PRACTICE_WORDS), [])
  const mobileBrowser = useMemo(() => detectMobileBrowser(), [])
  const [phase, setPhase] = useState('welcome')
  const [adIndex, setAdIndex] = useState(0)
  const [practiceIndex, setPracticeIndex] = useState(0)
  const [trialIndex, setTrialIndex] = useState(0)
  const [results, setResults] = useState([])
  const [adResults, setAdResults] = useState([])
  const [practiceResults, setPracticeResults] = useState([])
  const [stimulusStartedAt, setStimulusStartedAt] = useState(null)
  const [deadlineExpired, setDeadlineExpired] = useState(false)
  const [readySeconds, setReadySeconds] = useState(5)
  const [saveError, setSaveError] = useState('')
  const [calibrationStatus, setCalibrationStatus] = useState('idle')
  const [calibrationError, setCalibrationError] = useState('')
  const [calibrationCounts, setCalibrationCounts] = useState(() => CALIBRATION_POINTS.map(() => 0))
  const [facePresenceStatus, setFacePresenceStatus] = useState('detecting')
  const [recordingError, setRecordingError] = useState('')
  const [startingAdRecording, setStartingAdRecording] = useState(false)
  const [postAnswers, setPostAnswers] = useState({ main_message: '', motivation: '', attitudes: {}, respondent_name: '' })
  const [postError, setPostError] = useState('')
  const timerRef = useRef(null)
  const responseLockedRef = useRef(false)
  const webgazerRef = useRef(null)
  const webgazerStartedRef = useRef(false)
  const calibrationCountsRef = useRef(CALIBRATION_POINTS.map(() => 0))
  const calibrationMetaRef = useRef(null)
  const facePresenceStatusRef = useRef('detecting')
  const faceDetectionStartedAtRef = useRef(0)
  const faceStableSinceRef = useRef(0)
  const lastFaceSeenAtRef = useRef(0)
  const activeAdRecordingRef = useRef(null)
  const pendingRecordingUploadsRef = useRef([])
  const adRecordingMetadataRef = useRef({})
  const currentAd = adOrder[adIndex]
  const trials = trialSets[adIndex]

  const clearTimer = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  const schedule = useCallback((callback, ms) => {
    clearTimer()
    timerRef.current = window.setTimeout(callback, ms)
  }, [clearTimer])

  useEffect(() => () => clearTimer(), [clearTimer])

  useEffect(() => {
    if (phase !== 'practice-stimulus') return
    responseLockedRef.current = false
    setStimulusStartedAt(performance.now())
  }, [phase, practiceIndex])

  const showFixation = useCallback((nextPhase) => {
    setPhase('fixation')
    schedule(() => setPhase(nextPhase), randomBetween(250, 300))
  }, [schedule])

  const shutdownEyeTracking = useCallback(() => {
    if (!webgazerStartedRef.current) return
    const tracker = webgazerRef.current
    try { tracker?.clearGazeListener() } catch (error) { console.warn(error) }
    try { tracker?.stopVideo() } catch (error) { console.warn(error) }
    try { tracker?.end() } catch (error) { console.warn(error) }
    webgazerStartedRef.current = false
  }, [])

  const updateFacePresence = useCallback((hasFace) => {
    const now = performance.now()
    if (hasFace) {
      lastFaceSeenAtRef.current = now
      if (!faceStableSinceRef.current) faceStableSinceRef.current = now
      if (now - faceStableSinceRef.current >= FACE_CONFIRMATION_MS && facePresenceStatusRef.current !== 'present') {
        facePresenceStatusRef.current = 'present'
        setFacePresenceStatus('present')
      }
      return
    }
    faceStableSinceRef.current = 0
    const lastDetection = lastFaceSeenAtRef.current || faceDetectionStartedAtRef.current
    if (now - lastDetection >= FACE_MISSING_WARNING_MS && facePresenceStatusRef.current !== 'missing') {
      facePresenceStatusRef.current = 'missing'
      setFacePresenceStatus('missing')
    }
  }, [])

  const stopAdRecording = useCallback(() => {
    const recording = activeAdRecordingRef.current
    if (!recording) return
    activeAdRecordingRef.current = null
    const endedAt = new Date().toISOString()
    const baseMetadata = {
      advertisement_id: recording.adId,
      status: 'recorded',
      started_at: recording.startedAt,
      ended_at: endedAt,
      duration_ms: Math.round(performance.now() - recording.startedAtPerformance),
      viewport: recording.viewport,
      gaze_sample_count: recording.samples.length,
      gaze_samples: recording.samples,
    }
    if (!recording.recorder) {
      adRecordingMetadataRef.current = { ...adRecordingMetadataRef.current, [recording.adId]: { ...baseMetadata, camera_status: recording.cameraStatus || 'unavailable' } }
      return
    }
    const uploadPromise = new Promise((resolve) => {
      const finish = async () => {
        const contentType = recording.recorder.mimeType || recording.mimeType || 'video/webm'
        const blob = new Blob(recording.chunks, { type: contentType })
        if (!blob.size) {
          adRecordingMetadataRef.current = { ...adRecordingMetadataRef.current, [recording.adId]: { ...baseMetadata, camera_status: 'empty', camera_content_type: contentType, camera_byte_size: 0 } }
          resolve()
          return
        }
        try {
          const uploaded = await uploadSurveyCameraRecording({
            surveyCode: SURVEY_CODE,
            responseId,
            questionNumber: 1,
            stepId: 'ad_exposure',
            blob,
            extension: recording.extension,
          })
          adRecordingMetadataRef.current = { ...adRecordingMetadataRef.current, [recording.adId]: { ...baseMetadata, camera_status: 'uploaded', camera_recording: uploaded } }
        } catch (error) {
          adRecordingMetadataRef.current = { ...adRecordingMetadataRef.current, [recording.adId]: { ...baseMetadata, camera_status: 'upload_failed', camera_content_type: contentType, camera_byte_size: blob.size, error: error.message } }
        }
        resolve()
      }
      recording.recorder.addEventListener('stop', finish, { once: true })
      try { recording.recorder.stop() } catch (error) { void finish() }
    })
    pendingRecordingUploadsRef.current.push(uploadPromise)
  }, [responseId])

  const startAdRecording = useCallback(async () => {
    if (activeAdRecordingRef.current) return Boolean(activeAdRecordingRef.current.recorder)
    const startedAt = new Date().toISOString()
    const recording = {
      adId: currentAd.id,
      startedAt,
      startedAtPerformance: performance.now(),
      viewport: { width: window.innerWidth, height: window.innerHeight, device_pixel_ratio: window.devicePixelRatio || 1 },
      samples: [],
      recorder: null,
      chunks: [],
    }
    activeAdRecordingRef.current = recording
    try {
      await webgazerRef.current?.resume?.()
      const format = getCameraRecordingFormat()
      if (!format) throw new Error('MediaRecorder is not supported')
      const videoElement = document.getElementById('webgazerVideoFeed')
      const stream = videoElement?.srcObject
      const tracks = stream?.getVideoTracks?.() || []
      if (!tracks.length) throw new Error('Eye-tracking camera stream is unavailable')
      const recorder = new MediaRecorder(new MediaStream(tracks), {
        videoBitsPerSecond: 650000,
        ...(format.mimeType ? { mimeType: format.mimeType } : {}),
      })
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data?.size) recording.chunks.push(event.data)
      })
      recorder.start(500)
      recording.recorder = recorder
      recording.extension = format.extension
      recording.mimeType = recorder.mimeType || format.mimeType
      return true
    } catch (error) {
      recording.cameraStatus = `failed_to_start: ${error.message}`
      activeAdRecordingRef.current = null
      setRecordingError('Δεν ήταν δυνατή η έναρξη της εγγραφής βίντεο. Ελέγξτε την άδεια κάμερας και δοκιμάστε ξανά.')
      return false
    }
  }, [currentAd.id])

  const startEyeTrackingCalibration = useCallback(async () => {
    setCalibrationStatus('starting')
    setCalibrationError('')
    calibrationCountsRef.current = CALIBRATION_POINTS.map(() => 0)
    setCalibrationCounts([...calibrationCountsRef.current])
    facePresenceStatusRef.current = 'detecting'
    setFacePresenceStatus('detecting')
    faceDetectionStartedAtRef.current = performance.now()
    faceStableSinceRef.current = 0
    lastFaceSeenAtRef.current = 0
    try {
      const webgazerModule = await import('../webgazer/webgazer')
      const tracker = webgazerModule.default
      webgazerRef.current = tracker
      if (!tracker.detectCompatibility()) throw new Error('Camera API is not supported')
      webgazerStartedRef.current = true
      tracker
        .saveDataAcrossSessions(false)
        .setRegression('ridge')
        .setTracker('TFFacemesh')
        .applyKalmanFilter(true)
        .showVideoPreview(true)
        .showPredictionPoints(false)
        .setGazeListener((data, elapsedTime) => {
          const facePositions = tracker.getTracker()?.getPositions?.()
          updateFacePresence(Array.isArray(facePositions) && facePositions.length > 0)
          const recording = activeAdRecordingRef.current
          if (!recording || !data || !Number.isFinite(data.x) || !Number.isFinite(data.y)) return
          if (recording.samples.length >= MAX_GAZE_SAMPLES) return
          recording.samples.push({
            x: Math.round(data.x * 100) / 100,
            y: Math.round(data.y * 100) / 100,
            t_ms: Math.round(performance.now() - recording.startedAtPerformance),
            webgazer_time_ms: Math.round(elapsedTime),
          })
        })
      await tracker.clearData()
      await tracker.begin()
      tracker.removeMouseEventListeners()
      const previewWidth = Math.max(180, Math.min(300, window.innerWidth - 32))
      tracker.setVideoViewerSize(previewWidth, Math.round(previewWidth * 0.75))
      setCalibrationStatus('camera')
    } catch (error) {
      shutdownEyeTracking()
      setCalibrationError('Δεν ήταν δυνατή η εκκίνηση της κάμερας ή της παρακολούθησης βλέμματος.')
      setCalibrationStatus('error')
    }
  }, [shutdownEyeTracking, updateFacePresence])

  const finishCalibration = useCallback(() => {
    calibrationMetaRef.current = {
      status: 'completed',
      completed_at: new Date().toISOString(),
      required_clicks_per_point: CALIBRATION_CLICKS_PER_POINT,
      points: CALIBRATION_POINTS.map((point, index) => ({ x_percent: point.x, y_percent: point.y, clicks: calibrationCountsRef.current[index] })),
    }
    webgazerRef.current?.showVideoPreview(false).showPredictionPoints(false)
    setCalibrationStatus('completed')
  }, [])

  const recordCalibrationPoint = useCallback((pointIndex, event) => {
    if (calibrationCountsRef.current[pointIndex] >= CALIBRATION_CLICKS_PER_POINT) return
    const bounds = event.currentTarget.getBoundingClientRect()
    webgazerRef.current?.recordScreenPosition(bounds.left + bounds.width / 2, bounds.top + bounds.height / 2, 'click')
    calibrationCountsRef.current[pointIndex] += 1
    const nextCounts = [...calibrationCountsRef.current]
    setCalibrationCounts(nextCounts)
    if (nextCounts.every((count) => count >= CALIBRATION_CLICKS_PER_POINT)) window.setTimeout(finishCalibration, 250)
  }, [finishCalibration])

  useEffect(() => () => {
    stopAdRecording()
    shutdownEyeTracking()
  }, [shutdownEyeTracking, stopAdRecording])

  useEffect(() => {
    if (phase !== 'stimulus') return undefined
    responseLockedRef.current = false
    setStimulusStartedAt(performance.now())
    const stimulusTimer = window.setTimeout(() => {
      if (!responseLockedRef.current) setDeadlineExpired(true)
    }, DEADLINE_MS)
    return () => window.clearTimeout(stimulusTimer)
  }, [phase])

  useEffect(() => {
    if (phase === 'stimulus') setDeadlineExpired(false)
  }, [phase, trialIndex])

  useEffect(() => {
    if (phase !== 'prepare' && phase !== 'practice-prepare') return undefined
    const countdown = window.setInterval(() => {
      setReadySeconds((current) => Math.max(1, current - 1))
    }, 1000)
    const startTimer = window.setTimeout(() => {
      window.clearInterval(countdown)
      showFixation(phase === 'practice-prepare' ? 'practice-stimulus' : 'stimulus')
    }, 5000)
    return () => {
      window.clearInterval(countdown)
      window.clearTimeout(startTimer)
    }
  }, [phase, showFixation])

  useEffect(() => {
    if (phase === 'intertrial' && trialIndex >= trials.length) {
      clearTimer()
      setPhase('post-message')
    }
  }, [clearTimer, phase, practiceIndex, practiceTrials.length, trialIndex, trials.length])

  const respond = useCallback((response) => {
    if ((phase !== 'practice-stimulus' && phase !== 'stimulus') || responseLockedRef.current) return
    responseLockedRef.current = true
    clearTimer()
    const rtMs = Math.round(performance.now() - stimulusStartedAt)
    if (phase === 'practice-stimulus') {
      setPracticeResults((current) => [...current, { stimulus: practiceTrials[practiceIndex], response, rt_ms: rtMs, timed_out: false }])
      if (practiceIndex + 1 >= practiceTrials.length) {
        setPhase('practice-complete')
      } else {
        setPracticeIndex((current) => current + 1)
        showFixation('practice-stimulus')
      }
      return
    }
    const trial = trials[trialIndex]
    setResults((current) => [...current, { ...trial, response, rt_ms: rtMs, timed_out: deadlineExpired || rtMs > DEADLINE_MS }])
    setTrialIndex((current) => current + 1)
    setPhase('intertrial')
    schedule(() => showFixation('stimulus'), randomBetween(250, 400))
  }, [clearTimer, deadlineExpired, phase, practiceIndex, practiceTrials, schedule, showFixation, stimulusStartedAt, trialIndex, trials])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code === 'KeyI') respond('matches')
      if (event.code === 'KeyE') respond('does_not_match')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [respond])

  useEffect(() => {
    if (phase !== 'saving') return
    const save = async () => {
      try {
        await Promise.allSettled(pendingRecordingUploadsRef.current)
        const missingVideos = adOrder.filter((advertisement) => {
          const recording = adRecordingMetadataRef.current[advertisement.id]
          return recording?.camera_status !== 'uploaded' || !recording?.camera_recording?.storage_path
        })
        if (missingVideos.length) {
          setSaveError('Η συνέντευξη δεν αποθηκεύτηκε επειδή δεν ολοκληρώθηκε η υποχρεωτική εγγραφή βίντεο. Ξεκινήστε ξανά και βεβαιωθείτε ότι η κάμερα και η σύνδεση στο διαδίκτυο λειτουργούν.')
          setPhase('video-save-error')
          return
        }
        await saveSurveyResult({
          surveyCode: SURVEY_CODE,
          responseId,
          result: {
            survey_code: SURVEY_CODE,
            response_id: responseId,
            status: 'completed',
            language: 'el',
            completed_at: new Date().toISOString(),
            settings: { deadline_ms: DEADLINE_MS, fixation_ms: '250-300', intertrial_ms: '250-400', attribute_count_per_ad: ATTRIBUTES.length * 4 },
            advertisement_order: adOrder.map((advertisement) => advertisement.id),
            practice_trials: practiceResults,
            advertisement_tests: adResults,
            respondent_name: postAnswers.respondent_name,
            eye_tracking: {
              engine: 'webgazer',
              calibration: calibrationMetaRef.current,
              ad_exposures: adRecordingMetadataRef.current,
            },
          },
        })
        setPhase('complete')
      } catch (error) {
        setSaveError('Δεν ήταν δυνατή η αποθήκευση των απαντήσεών σας. Ελέγξτε τη σύνδεσή σας και προσπαθήστε ξανά.')
        setPhase('save-error')
      }
    }
    save()
  }, [adOrder, adResults, phase, postAnswers.respondent_name, practiceResults, responseId])

  const beginPractice = () => {
    setReadySeconds(5)
    setPhase('practice-prepare')
  }
  const beginAd = async () => {
    if (startingAdRecording) return
    setStartingAdRecording(true)
    setRecordingError('')
    const started = await startAdRecording()
    setStartingAdRecording(false)
    if (started) setPhase('ad')
    else setPhase('recording-error')
  }
  const startTestPreparation = () => {
    setReadySeconds(5)
    setPhase('prepare')
  }
  const onAdEnded = () => {
    stopAdRecording()
    webgazerRef.current?.pause?.()
    if (adIndex === adOrder.length - 1) window.setTimeout(shutdownEyeTracking, 300)
    setPhase('test-instructions')
  }

  const storeCurrentAdvertisementTest = () => {
    const { respondent_name, ...questionnaire } = postAnswers
    setAdResults((current) => [...current, {
      advertisement_id: currentAd.id,
      advertisement_url: currentAd.url,
      trials: results,
      questionnaire,
    }])
  }

  const continueFromMessage = () => {
    if (!postAnswers.main_message.trim()) {
      setPostError('Παρακαλούμε γράψτε την απάντησή σας πριν συνεχίσετε.')
      return
    }
    setPostError('')
    setPhase('post-motivation')
  }

  const continueFromMotivation = () => {
    if (!postAnswers.motivation) {
      setPostError('Παρακαλούμε επιλέξτε μία απάντηση πριν συνεχίσετε.')
      return
    }
    setPostError('')
    setPhase('post-attitudes')
  }

  const continueFromAttitudes = () => {
    if (Object.keys(postAnswers.attitudes).length !== ATTITUDE_STATEMENTS.length) {
      setPostError('Παρακαλούμε επιλέξτε μία απάντηση για κάθε πρόταση.')
      return
    }
    setPostError('')
    if (adIndex < adOrder.length - 1) {
      storeCurrentAdvertisementTest()
      setAdIndex((current) => current + 1)
      setTrialIndex(0)
      setResults([])
      setPostAnswers({ main_message: '', motivation: '', attitudes: {}, respondent_name: '' })
      setPhase('next-ad')
      return
    }
    setPhase('post-name')
  }

  const savePostQuestionnaire = () => {
    if (!postAnswers.respondent_name.trim()) {
      setPostError('Παρακαλούμε γράψτε το όνομά σας πριν ολοκληρώσετε.')
      return
    }
    setPostError('')
    storeCurrentAdvertisementTest()
    setPhase('saving')
  }

  if (mobileBrowser) return <Screen><h1>Απαιτείται υπολογιστής</h1><p>Η μελέτη χρησιμοποιεί eye tracking μέσω κάμερας και μπορεί να ολοκληρωθεί μόνο από επιτραπέζιο ή φορητό υπολογιστή.</p><p>Παρακαλούμε ανοίξτε αυτόν τον σύνδεσμο σε browser υπολογιστή για να συνεχίσετε.</p></Screen>

  if (calibrationStatus !== 'completed') {
    const completedClicks = calibrationCounts.reduce((total, count) => total + count, 0)
    const faceReady = facePresenceStatus === 'present'
    const faceStatusText = facePresenceStatus === 'present'
      ? 'Το πρόσωπό σας εντοπίστηκε. Μπορείτε να συνεχίσετε.'
      : facePresenceStatus === 'missing'
        ? 'Δεν βλέπουμε το πρόσωπό σας. Κοιτάξτε προς την κάμερα και μείνετε μέσα στο κάδρο.'
        : 'Αναζήτηση προσώπου στην κάμερα…'
    if (calibrationStatus === 'active') return <main className="rt-calibration"><section className="rt-calibration-card"><h1>Βαθμονόμηση παρακολούθησης βλέμματος</h1><p>Κοιτάξτε απευθείας κάθε σημείο και κάντε κλικ πέντε φορές. Κρατήστε το κεφάλι σας ακίνητο και μετακινείτε μόνο τα μάτια σας.</p><p className={`rt-face-status ${facePresenceStatus}`}>{faceStatusText}</p><strong>Ολοκληρώθηκαν {completedClicks} από {CALIBRATION_POINTS.length * CALIBRATION_CLICKS_PER_POINT} κλικ βαθμονόμησης</strong></section>{facePresenceStatus === 'missing' && <FaceWarning />}{CALIBRATION_POINTS.map((point, index) => { const count = calibrationCounts[index]; const done = count >= CALIBRATION_CLICKS_PER_POINT; return <button key={`${point.x}-${point.y}`} className={`rt-calibration-point${done ? ' done' : ''}`} style={{ left: `${point.x}%`, top: `${point.y}%` }} disabled={done || !faceReady} onClick={(event) => recordCalibrationPoint(index, event)} aria-label={`Σημείο βαθμονόμησης ${index + 1}`}>{done ? '✓' : CALIBRATION_CLICKS_PER_POINT - count}</button> })}</main>
    if (calibrationStatus === 'camera') return <Screen><h1>Έλεγχος κάμερας</h1><p>Κρατήστε το πρόσωπό σας στο κέντρο του πλαισίου της κάμερας. Όταν είστε έτοιμοι, ξεκινήστε τη βαθμονόμηση παρακολούθησης βλέμματος.</p><p className={`rt-face-status ${facePresenceStatus}`}>{faceStatusText}</p><p className="rt-recording-notice">Το βίντεο της κάμερας και τα δεδομένα του βλέμματος καταγράφονται μόνο κατά την προβολή της διαφήμισης.</p><button disabled={!faceReady} onClick={() => { webgazerRef.current?.showVideoPreview(false).showPredictionPoints(false); setCalibrationStatus('active') }}>Έναρξη βαθμονόμησης</button></Screen>
    if (calibrationStatus === 'starting') return <Screen><div className="rt-loader" /><p>Προετοιμασία κάμερας για παρακολούθηση βλέμματος…</p></Screen>
    if (calibrationStatus === 'error') return <Screen><h1>Δεν ήταν δυνατή η εκκίνηση της κάμερας</h1><p>{calibrationError}</p><p>Η κάμερα και η εγγραφή βίντεο είναι υποχρεωτικές για να συνεχίσετε σε αυτή τη μελέτη.</p><button onClick={startEyeTrackingCalibration}>Δοκιμάστε ξανά</button></Screen>
    return <Screen><h1>Καλώς ήρθατε στη μελέτη</h1><p>Αυτή η σύντομη μελέτη διερευνά τις εντυπώσεις που δημιουργεί μια ηχητική διαφήμιση και τον τρόπο με τον οποίο αντιδρούν οι άνθρωποι σε αυτήν.</p><div className="rt-study-overview"><div><b>1. Βαθμονόμηση παρακολούθησης βλέμματος</b><span>Αρχικά θα ρυθμίσουμε και θα βαθμονομήσουμε την παρακολούθηση βλέμματος μέσω κάμερας.</span></div><div><b>2. Εξάσκηση απαντήσεων</b><span>Θα εξασκηθείτε στη χρήση των πλήκτρων απάντησης πριν αρχίσει η κύρια δοκιμή.</span></div><div><b>3. Δοκιμή χρόνου αντίδρασης</b><span>Αφού ακούσετε τη διαφήμιση, θα απαντήσετε γρήγορα σε μια σειρά λέξεων.</span></div><div><b>4. Τελικές ερωτήσεις</b><span>Στη συνέχεια θα απαντήσετε σε λίγες σύντομες ερωτήσεις για τη διαφήμιση.</span></div></div><p>Δεν υπάρχουν σωστές ή λάθος απαντήσεις. Ακολουθήστε τις οδηγίες και απαντήστε φυσικά.</p><button onClick={startEyeTrackingCalibration}>Έναρξη βαθμονόμησης κάμερας</button></Screen>
  }

  if (phase === 'welcome') return <Screen><h1>Δοκιμή αντίδρασης στη διαφήμιση</h1><p>Αρχικά θα εξασκηθείτε στα κουμπιά απάντησης. Κάθε λέξη θα παραμείνει στην οθόνη μέχρι να απαντήσετε.</p><p>Επιλέξτε <strong>ΤΑΙΡΙΑΖΕΙ</strong> αν η λέξη ταιριάζει με την εντύπωση της διαφήμισης ή <strong>ΔΕΝ ΤΑΙΡΙΑΖΕΙ</strong> αν δεν ταιριάζει. Μπορείτε να κάνετε κλικ στα κουμπιά ή να χρησιμοποιήσετε τα πλήκτρα <strong>Ι</strong> και <strong>Ε</strong>.</p><button onClick={beginPractice}>Έναρξη εξάσκησης</button></Screen>
  if (phase === 'practice-prepare') return <Screen><h1>Ετοιμαστείτε για την εξάσκηση</h1><p>Τοποθετήστε το αριστερό σας δάχτυλο στο <strong>Ε</strong> και το δεξί σας δάχτυλο στο <strong>Ι</strong>. Η εξάσκηση ξεκινά σε λίγο.</p><div className="rt-countdown" aria-label={`Απομένουν ${readySeconds} δευτερόλεπτα`}>{readySeconds}</div></Screen>
  if (phase === 'practice-complete') return <Screen><h1>Η εξάσκηση ολοκληρώθηκε</h1><p>Στη συνέχεια η διαφήμιση θα προβληθεί μία φορά χωρίς διακοπή. Μετά το τέλος της αρχίζει η κύρια δοκιμή. Κάθε λέξη θα εμφανίζεται για έως 1,8 δευτερόλεπτα.</p><button disabled={startingAdRecording} onClick={beginAd}>{startingAdRecording ? 'Προετοιμασία εγγραφής βίντεο…' : 'Προβολή διαφήμισης'}</button></Screen>
  if (phase === 'next-ad') return <Screen><h1>Το πρώτο μέρος ολοκληρώθηκε</h1><p>Στη συνέχεια θα ακούσετε μία δεύτερη διαφήμιση. Θα ακολουθήσει η ίδια δοκιμή λέξεων και ένα σύντομο σύνολο ερωτήσεων.</p><p>Δεν χρειάζεται να επαναλάβετε τη βαθμονόμηση ή την εξάσκηση.</p><button disabled={startingAdRecording} onClick={beginAd}>{startingAdRecording ? 'Προετοιμασία εγγραφής βίντεο…' : 'Προβολή επόμενης διαφήμισης'}</button></Screen>
  if (phase === 'recording-error') return <Screen><h1>Η εγγραφή βίντεο δεν ξεκίνησε</h1><p>{recordingError}</p><p>Η εγγραφή βίντεο είναι υποχρεωτική. Ελέγξτε την άδεια κάμερας και δοκιμάστε ξανά την προετοιμασία.</p><button onClick={() => { shutdownEyeTracking(); startEyeTrackingCalibration() }}>Επανάληψη ρύθμισης κάμερας</button></Screen>
  if (phase === 'ad') return <main className="rt-video"><video autoPlay playsInline onEnded={onAdEnded} src={currentAd.url} />{facePresenceStatus === 'missing' && <FaceWarning />}</main>
  if (phase === 'test-instructions') return <Screen><h1>Έτοιμοι για τη δοκιμή;</h1><p>Επιλέξτε τις λέξεις που ταιριάζουν με την εντύπωση που δημιούργησε η διαφήμιση που μόλις ακούσατε.</p><p>Χρησιμοποιήστε το <strong>Ε</strong> για <strong>ΔΕΝ ΤΑΙΡΙΑΖΕΙ</strong> και το <strong>Ι</strong> για <strong>ΤΑΙΡΙΑΖΕΙ</strong>.</p><button onClick={startTestPreparation}>Έναρξη δοκιμής</button></Screen>
  if (phase === 'prepare') return <Screen><h1>Ετοιμαστείτε</h1><p>Τοποθετήστε το αριστερό σας δάχτυλο στο <strong>Ε</strong> και το δεξί σας δάχτυλο στο <strong>Ι</strong>. Η δοκιμή ξεκινά σε λίγο.</p><div className="rt-countdown" aria-label={`Απομένουν ${readySeconds} δευτερόλεπτα`}>{readySeconds}</div></Screen>
  if (phase === 'post-message') return <QuestionScreen step="1 από 4"><h1>Ερωτήσεις μετά από τη διαφήμιση</h1><h2>Ποιο θεωρείτε ότι είναι το βασικό μήνυμα που θέλει να σας μεταδώσει αυτή η διαφήμιση;</h2><p>Γράψτε την απάντησή σας με δικά σας λόγια.</p><textarea className="rt-open-answer" value={postAnswers.main_message} onChange={(event) => setPostAnswers((current) => ({ ...current, main_message: event.target.value }))} aria-label="Βασικό μήνυμα" />{postError && <p className="rt-question-error">{postError}</p>}<button onClick={continueFromMessage}>Συνέχεια</button></QuestionScreen>
  if (phase === 'post-motivation') return <QuestionScreen step="2 από 4"><h1>Ενεργοποίηση για περαιτέρω αναζήτηση</h1><h2>Σε ποιο βαθμό αυτή η διαφήμιση θα σας παρακινούσε να αναζητήσετε περισσότερες πληροφορίες για το στεγαστικό δάνειο της Eurobank;</h2><div className="rt-scale-options">{MOTIVATION_OPTIONS.map((label, index) => <label key={label}><input type="radio" name="motivation" checked={postAnswers.motivation === String(index + 1)} onChange={() => setPostAnswers((current) => ({ ...current, motivation: String(index + 1) }))} /><span><b>{index + 1}.</b> {label}</span></label>)}</div>{postError && <p className="rt-question-error">{postError}</p>}<button onClick={continueFromMotivation}>Συνέχεια</button></QuestionScreen>
  if (phase === 'post-attitudes') return <QuestionScreen step="3 από 4"><h1>Δηλώσεις αξιολόγησης</h1><p>Με βάση αποκλειστικά το σενάριο που μόλις ακούσατε και την εντύπωση που σας δημιούργησε, σε ποιο βαθμό συμφωνείτε ή διαφωνείτε με καθεμία από τις παρακάτω προτάσεις;</p><div className="rt-matrix-wrap"><table className="rt-matrix-table"><thead><tr><th scope="col">Πρόταση</th>{AGREEMENT_OPTIONS.map((label, index) => <th key={label} scope="col"><b>{index + 1}</b><span>{label}</span></th>)}</tr></thead><tbody>{ATTITUDE_STATEMENTS.map((statement, rowIndex) => <tr key={statement}><th scope="row"><span>{rowIndex + 1}</span>{statement}</th>{AGREEMENT_OPTIONS.map((label, optionIndex) => <td key={label}><label aria-label={`${optionIndex + 1}. ${label}`}><input type="radio" name={`attitude-${rowIndex}`} checked={postAnswers.attitudes[rowIndex] === String(optionIndex + 1)} onChange={() => setPostAnswers((current) => ({ ...current, attitudes: { ...current.attitudes, [rowIndex]: String(optionIndex + 1) } }))} /><span>{optionIndex + 1}</span></label></td>)}</tr>)}</tbody></table></div>{postError && <p className="rt-question-error">{postError}</p>}<button onClick={continueFromAttitudes}>Συνέχεια</button></QuestionScreen>
  if (phase === 'post-name') return <QuestionScreen step="4 από 4"><h1>Μία τελευταία ερώτηση</h1><h2>Παρακαλούμε γράψτε το όνομά σας.</h2><input className="rt-name-answer" type="text" autoComplete="name" value={postAnswers.respondent_name} onChange={(event) => setPostAnswers((current) => ({ ...current, respondent_name: event.target.value }))} aria-label="Το όνομά σας" />{postError && <p className="rt-question-error">{postError}</p>}<button onClick={savePostQuestionnaire}>Ολοκλήρωση</button></QuestionScreen>
  if (phase === 'saving') return <Screen><div className="rt-loader" /><p>Αποθήκευση των απαντήσεών σας…</p></Screen>
  if (phase === 'video-save-error') return <Screen><h1>Η εγγραφή βίντεο δεν ολοκληρώθηκε</h1><p>{saveError}</p><button onClick={() => window.location.reload()}>Επανεκκίνηση μελέτης</button></Screen>
  if (phase === 'complete') return <Screen><h1>Ευχαριστούμε</h1><p>Οι απαντήσεις σας καταγράφηκαν.</p></Screen>
  if (phase === 'save-error') return <Screen><h1>Σχεδόν ολοκληρώθηκε</h1><p>{saveError}</p><button onClick={() => setPhase('saving')}>Προσπαθήστε ξανά</button></Screen>
  if (phase === 'fixation' || phase === 'transition' || phase === 'intertrial') return <main className="rt-blank">{phase === 'fixation' && <span>+</span>}</main>

  const isPractice = phase === 'practice-stimulus'
  const word = isPractice ? practiceTrials[practiceIndex] : trials[trialIndex]?.stimulus
  return <main className="rt-test" aria-live="polite"><div className="rt-progress">{isPractice ? `Εξάσκηση ${practiceIndex + 1} από ${practiceTrials.length}` : `${trialIndex + 1} από ${trials.length}`}</div><p className="rt-prompt">Η λέξη ταιριάζει με τη διαφήμιση;</p><h1>{word}</h1><div className="rt-actions"><button className="secondary" onClick={() => respond('does_not_match')}>ΔΕΝ ΤΑΙΡΙΑΖΕΙ <kbd>Ε</kbd></button><button onClick={() => respond('matches')}>ΤΑΙΡΙΑΖΕΙ <kbd>Ι</kbd></button></div><p className={deadlineExpired ? 'rt-hint rt-late' : 'rt-hint'}>{deadlineExpired ? 'Απαντήστε πιο γρήγορα στην επόμενη λέξη.' : 'Απαντήστε όσο πιο γρήγορα και με ακρίβεια μπορείτε.'}</p></main>
}

function Screen({ children }) {
  return <main className="rt-screen">{children}</main>
}

function QuestionScreen({ step, children }) {
  return <main className="rt-question-screen"><div className="rt-question-step">{step}</div><section>{children}</section></main>
}

function FaceWarning() {
  return <div className="rt-face-warning" role="alert"><strong>Δεν εντοπίζεται το πρόσωπό σας</strong><span>Κοιτάξτε προς την κάμερα και κρατήστε το πρόσωπό σας ορατό.</span></div>
}
